import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Grid
} from '@mui/material';
import analyticsService from '../../services/analyticsService';

const AlertSettingsForm = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    alertQualityThreshold: 60,
    alertJitterThresholdMs: 30,
    alertRttThresholdMs: 300,
    alertPacketLossThresholdPct: 5.0,
    emailSummariesEnabled: true,
    inCallAlertsEnabled: true
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await analyticsService.getAlertThresholds();
      setSettings(data);
    } catch (err) {
      setError('Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      
      await analyticsService.updateAlertThresholds(settings);
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Analytics Alert Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Configure thresholds for quality alerts and notification preferences
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Settings saved successfully
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Quality Thresholds
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Alerts will be triggered when these thresholds are exceeded
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Quality Score Threshold"
                  type="number"
                  value={settings.alertQualityThreshold}
                  onChange={(e) => handleChange('alertQualityThreshold', parseInt(e.target.value))}
                  inputProps={{ min: 0, max: 100 }}
                  helperText="Below this score triggers alerts (0-100)"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Jitter Threshold (ms)"
                  type="number"
                  value={settings.alertJitterThresholdMs}
                  onChange={(e) => handleChange('alertJitterThresholdMs', parseInt(e.target.value))}
                  inputProps={{ min: 0 }}
                  helperText="Above this value triggers alerts"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="RTT Threshold (ms)"
                  type="number"
                  value={settings.alertRttThresholdMs}
                  onChange={(e) => handleChange('alertRttThresholdMs', parseInt(e.target.value))}
                  inputProps={{ min: 0 }}
                  helperText="Round-trip time alert threshold"
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Packet Loss Threshold (%)"
                  type="number"
                  value={settings.alertPacketLossThresholdPct}
                  onChange={(e) => handleChange('alertPacketLossThresholdPct', parseFloat(e.target.value))}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText="Packet loss percentage threshold"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Notification Preferences
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={settings.inCallAlertsEnabled}
                  onChange={(e) => handleChange('inCallAlertsEnabled', e.target.checked)}
                />
              }
              label="Enable In-Call Quality Alerts"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mb: 2 }}>
              Show real-time notifications when quality issues are detected during meetings
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={settings.emailSummariesEnabled}
                  onChange={(e) => handleChange('emailSummariesEnabled', e.target.checked)}
                />
              }
              label="Enable Post-Meeting Email Summaries"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
              Automatically send email summaries to hosts after meetings end
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={loadSettings}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Settings'}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default AlertSettingsForm;
